import Vue from 'vue'
import App from './App.vue'
// import './registerServiceWorker'  // Temporarily disabled for Vite migration
import router from './router'
import store from './store'
import vuetify from './plugins/vuetify'
import '@mdi/font/css/materialdesignicons.css' // Material Design Icons for Vuetify
import './assets/css/accessibility.css' // Styles d'accessibilité
import moment from 'moment'
import 'moment/locale/fr'
import 'moment/locale/en-gb'
// import VueIntro from 'vue-introjs';
// Vue.use(VueIntro);
// import 'intro.js/introjs.css';
import Hotjar from 'vue-hotjar'
import * as Sentry from "@sentry/vue";
import { BrowserTracing } from "@sentry/tracing";

// init event bus
export const EventBus = new Vue();

Vue.prototype.$bus = EventBus;

Vue.use (Hotjar, {
  id: '3403295' // Hotjar Site ID
})

Sentry.init({
  Vue,
  dsn: "https://0ad4382e059f4c2c887a3780aa37aab1@o569116.ingest.sentry.io/4504826655342592",
  integrations: [
    new BrowserTracing({
      routingInstrumentation: Sentry.vueRouterInstrumentation(router),
      tracePropagationTargets: ["localhost", "api.voxdub.com","api-2.voxdub.com","app.voxdub.com", /^\//],
    }),
  ],
  // Set tracesSampleRate to 1.0 to capture 100%
  // of transactions for performance monitoring.
  // We recommend adjusting this value in production
  tracesSampleRate: 1.0,
});

// import VueGtag from "vue-gtag";

// Vue.use(VueGtag, {
//   config: { id: "G-B6S8PNCRSS" }
// }, router);

import Strapi from "strapi-sdk-js";

const strapi = new Strapi({
  url: import.meta.env.VITE_APP_API_URI || "http://localhost:1337"
});

Vue.prototype.$strapi = strapi;
Vue.prototype.$sentry = Sentry;

moment().locale('fr')

import * as contentful from 'contentful'
Vue.prototype.$contentful = contentful

Vue.config.productionTip = false

Vue.prototype.$moment = moment

// Import the Auth0 configuration
// Should not import the named export 'clientId' (imported as 'clientId') from default-exporting module (only default export is available soon)
import { domain, clientId } from '../auth_config.json';

// Import the plugin here
import { Auth0Plugin } from './auth';
import axios from 'axios';

import i18n from './i18n'

// Import GrowthBook, Qonversion, Amplitude and OneSignal plugins
import GrowthBookPlugin, { updateUserAttributes, getLimits } from './plugins/growthbook'
import QonversionPlugin from './plugins/qonversion'
import AmplitudePlugin from './plugins/amplitude'
import { initOneSignal, identifyOneSignalUser } from './plugins/onesignal'

// Install GrowthBook
Vue.use(GrowthBookPlugin)
// Install Qonversion
Vue.use(QonversionPlugin)
// Install Amplitude
Vue.use(AmplitudePlugin)
// OneSignal est installé automatiquement dans onesignal.js via Vue.use(OneSignalVue)

// Install the authentication plugin here
Vue.use(Auth0Plugin, {
  domain,
  clientId,
  onRedirectCallback: appState => {
    // if appState target url contain t param
    if (appState && appState.targetUrl && appState.targetUrl.indexOf('t=') > -1) {
      // get the t param
      if(!localStorage.getItem('t')){
        const t = appState.targetUrl.split('t=')[1]
        localStorage.setItem('t', t)
        axios.post(`${import.meta.env.VITE_APP_API_URI}/api/affiliate-logs`, {code: t, type:'signup'})
      }
    }
    router.push(
      appState && appState.targetUrl
        ? appState.targetUrl
        : window.location.pathname
    );
  }
});

const app = new Vue({
  router,
  store,
  vuetify,
  i18n,
  render: h => h(App),
  async mounted() {
    // Charger l'usage depuis le localStorage
    this.$store.dispatch('loadUsageFromStorage')
    
    // Initialiser OneSignal (avant l'auth pour capturer les permissions)
    try {
      await initOneSignal()
      console.log('[VoxDub] OneSignal initialized')
    } catch (error) {
      console.error('[VoxDub] OneSignal initialization failed:', error)
    }
    
    // Attendre que l'auth soit prête
    this.$watch('$auth.isAuthenticated', async (isAuthenticated) => {
      if (isAuthenticated && this.$auth.user) {
        // Sur iOS Capacitor, forcer le rôle PRO (app payante)
        const isNativePlatform = window.Capacitor && window.Capacitor.isNativePlatform()
        if (isNativePlatform) {
          this.$auth.user.role = 'pro'
          console.log('[VoxDub] iOS native platform detected - User role forced to PRO')
        }
        
        // Identifier l'utilisateur dans Amplitude
        this.$amplitude.identify(
          this.$auth.user.sub || this.$auth.user.email,
          {
            email: this.$auth.user.email,
            name: this.$auth.user.name,
            role: this.$auth.user.role || 'free',
            created_at: this.$auth.user.created_at
          }
        )
        
        // Track login
        this.$amplitude.track('User_Login', {
          role: this.$auth.user.role || 'free'
        })
        
        // Identifier l'utilisateur dans OneSignal
        try {
          await identifyOneSignalUser(this.$auth.user)
          console.log('[VoxDub] OneSignal user identified')
        } catch (error) {
          console.error('[VoxDub] OneSignal user identification failed:', error)
        }
        
        // Mettre à jour GrowthBook avec les attributs utilisateur
        updateUserAttributes(this.$auth.user)
        
        // Initialiser Qonversion
        await this.$qonversion.init(this.$auth.user.sub || this.$auth.user.id)
        
        // Mettre à jour les limites basées sur le rôle et A/B tests
        const limits = getLimits(this.$auth.user.role)
        this.$store.dispatch('updateLimits', limits)
        
        console.log('[VoxDub] User initialized with role:', this.$auth.user.role, 'and limits:', limits)
      }
    }, { immediate: true })
    
    // Watcher additionnel pour détecter les changements de rôle après connexion
    // (par exemple après un achat Stripe)
    this.$watch('$auth.user.role', async (newRole, oldRole) => {
      if (newRole && newRole !== oldRole && this.$auth.isAuthenticated) {
        console.log('[VoxDub] User role changed from', oldRole, 'to', newRole, '- Updating limits...')
        
        // Recalculer les limites avec le nouveau rôle
        const limits = getLimits(newRole)
        this.$store.dispatch('updateLimits', limits)
        
        console.log('[VoxDub] Limits updated after role change:', limits)
        
        // Mettre à jour Amplitude avec le nouveau rôle
        if (this.$amplitude && this.$auth.user) {
          this.$amplitude.identify(
            this.$auth.user.sub || this.$auth.user.email,
            {
              email: this.$auth.user.email,
              name: this.$auth.user.name,
              role: newRole,
              created_at: this.$auth.user.created_at
            }
          )
        }
      }
    }, { deep: true })
  }
})

app.$mount('#app')
